import {useState} from 'react';

import {handleError, useAppDispatch, useAppSelector, withAsyncThunkErrorHandling} from '@lib/store';
import {actions} from '@lib/store/src/data/timeslots/actions';
import {
  getIsOfficeDayBookableBasedOnQuota,
  getOfficeDayDisabledMessage,
  getScheduleForDateIsUpdating,
} from '@lib/store/src/data/timeslots/selectors/index';
import {localDateStringToEndOfDay, localDateStringToStartOfDay} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {WorkdayStatus} from '@lib/store/src/data/timeslots/apiTypes';
import {WorkdayStatusButton} from './WorkdayStatusButton';
import {isBeforeToday} from '@lib/logic';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {useModal} from '@hooks';
import {BookingLocationStatusCard} from '../../bookWorkspace';

interface NoWorkdayContentProps {
  selectedDate: string;
}

export const NoWorkdayContent = ({selectedDate}: NoWorkdayContentProps) => {
  const dispatch = useAppDispatch();
  const {setModalPages, openModal} = useModal();
  const date = useMemoDate(selectedDate);

  const isPast = isBeforeToday(selectedDate);
  const isOfficeDayBookableBasedOnQuota = useAppSelector(getIsOfficeDayBookableBasedOnQuota(date));
  const officeDayDisabledMessage = useAppSelector((state) => getOfficeDayDisabledMessage(state, date));

  const [localStatus, setLocalStatus] = useState<WorkdayStatus | null>(null);

  const mutating = useAppSelector(getScheduleForDateIsUpdating(selectedDate));

  const handlePress = (status: WorkdayStatus) => {
    handleWorkdayStatus(status);
  };

  const handleWorkdayStatus = async (status: WorkdayStatus) => {
    setLocalStatus(status);

    const base = {
      localStart: localDateStringToStartOfDay(selectedDate),
      localEnd: localDateStringToEndOfDay(selectedDate),
    };

    if (status === 'OfficeDay') {
      setModalPages([
        <BookingLocationStatusCard
          date={selectedDate}
          workday={null}
        />,
      ]);
      openModal();

      return;
    }

    const {success, result} = await dispatch(
      withAsyncThunkErrorHandling(() => actions.createWorkday({...base, status})),
    );

    if (!success) {
      setLocalStatus(null);
      handleError(result, dispatch);
      return;
    }

    setLocalStatus(null);
  };

  if (isPast) {
    return null;
  }

  return (
    <div className="flex gap-3 mt-2 my-4">
      <WorkdayStatusButton
        status={'OfficeDay'}
        disabled={mutating || !!officeDayDisabledMessage || !isOfficeDayBookableBasedOnQuota}
        loading={mutating && localStatus === status}
        buttonClassName="rounded bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.05]"
        iconClassName={`${mutating ? 'opacity-60 cursor-not-allowed' : ''}`}
        onClick={() => handlePress('OfficeDay')}
        customTitle={officeDayDisabledMessage?.title}
        customSubtitle={officeDayDisabledMessage?.subtitle}
      />

      <WorkdayStatusButton
        status={'WorkingRemotely'}
        disabled={mutating}
        loading={mutating && localStatus === status}
        buttonClassName="rounded bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.05]"
        iconClassName={`${mutating ? 'opacity-60 cursor-not-allowed' : ''}`}
        onClick={() => handlePress('WorkingRemotely')}
      />

      <WorkdayStatusButton
        status={'NotWorking'}
        disabled={mutating}
        loading={mutating && localStatus === status}
        buttonClassName="rounded bg-mapiq-black-800*/[0.03] hover:bg-mapiq-black-800*/[0.05]"
        iconClassName={`${mutating ? 'opacity-60 cursor-not-allowed' : ''}`}
        onClick={() => handlePress('NotWorking')}
      />
    </div>
  );
};
